import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { StyleSelector } from '../components/StyleSelector';
import { UploadPanel } from '../components/UploadPanel';
import { ProcessingStatus, type ProcessingState } from '../components/ProcessingStatus';
import { useStyleCatalog } from '../api/useStyleCatalog';
import { useStyleTransferMutation } from '../hooks/useStyleTransferMutation';
import { useTransferSession } from '../hooks/useTransferSession';
import { refreshNetworkStatusSnapshot, useIsOnline } from '../../../shared/hooks/useNetworkStatus';
import type { Quota, StyleOption } from '../../../shared/api/contracts/styleTransferSchemas';
import { useLiveRegion } from '../a11y/liveRegions';
import { useOfflineUploadQueue } from '../hooks/useOfflineUploadQueue';
import { useHistoryStore, type HistoryEntry } from '../state/historyStore';
import { buildHistoryEntry } from '../storage/historyStorage';
import { HistoryPanel } from '../components/HistoryPanel';
import { recordTransferMetric } from '../../../shared/telemetry/transferMetrics';
import { MAX_IMAGE_FILE_SIZE_BYTES } from '../../../shared/constants/uploadConstraints';
import { validateImageFile } from '../../../shared/utils/validateImageFile';
import { UploadGate } from '../components/UploadGate';
import { QuotaBanner } from '../components/QuotaBanner';
import { useSessionStore } from '../state/sessionStore';
import { convertHeicToJpeg, isHeicLikeFile } from '../../../shared/utils/imageTranscoding';

interface StyleTransferExperienceProps {
  variant: 'guest' | 'member';
}

interface ProcessUploadOptions {
  fromQueue?: boolean;
  backgroundColorCode?: string | null;
}

export function StyleTransferExperience({ variant }: StyleTransferExperienceProps) {
  const isMemberVariant = variant === 'member';
  const transferNamespace = 'transfer';
  const { t } = useTranslation();
  const styleCatalog = useStyleCatalog();
  const {
    data: catalogData,
    hasNextPage,
    fetchNextPage,
    isLoading: catalogIsLoading,
    isFetchingNextPage
  } = styleCatalog;
  const catalogPages = catalogData?.pages ?? [];
  const visibleStyles = useMemo(
    () =>
      catalogPages.flatMap((page, pageIndex) => {
        const batchId =
          typeof page.pageInfo.page === 'number' ? String(page.pageInfo.page) : String(pageIndex);
        return page.items.map((style) => ({
          ...style,
          uiBatchId: batchId
        }));
      }),
    [catalogPages]
  );
  const totalReportedStyles = catalogPages.length
    ? catalogPages[catalogPages.length - 1]?.pageInfo.totalElements ?? visibleStyles.length
    : visibleStyles.length;
  const allStyles = visibleStyles;
  const [autoAdvanceInFlight, setAutoAdvanceInFlight] = useState(false);
  const stylesLoading = catalogIsLoading && !catalogPages.length;
  const mutation = useStyleTransferMutation();
  const isInteractionLocked = mutation.isPending;
  const { ensureToken, gate, token, quota } = useTransferSession(variant);
  const updateQuota = useSessionStore((state) => state.updateQuota);
  const setQuotaStatus = useSessionStore((state) => state.setQuotaStatus);
  const announce = useLiveRegion();
  const getOnline = useIsOnline();
  const historySectionRef = useRef<HTMLDivElement | null>(null);
  const stylePickerRef = useRef<HTMLDivElement | null>(null);
  const uploadPanelRef = useRef<HTMLDivElement | null>(null);
  const sizeLimitLabel = `${Math.round(MAX_IMAGE_FILE_SIZE_BYTES / (1024 * 1024))} MB`;
  const numberFormatter = useMemo(
    () =>
      new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 1
      }),
    []
  );

  const gateAllowed = gate.allowed;
  const gateReasonKey = gate.reasonKey;
  const gateErrorKey = gate.errorKey;

  const [selectedStyleId, setSelectedStyleId] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [processingState, setProcessingState] = useState<ProcessingState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [etaSeconds, setEtaSeconds] = useState<number | undefined>();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [activeUploadController, setActiveUploadController] = useState<AbortController | null>(null);
  const [countdownSeconds, setCountdownSeconds] = useState<number | null>(null);
  const [showExtendedWaitNotice, setShowExtendedWaitNotice] = useState(false);
  const [isPreparingImage, setIsPreparingImage] = useState(false);
  const [conversionMessage, setConversionMessage] = useState<string | null>(null);
  const [convertedFromHeic, setConvertedFromHeic] = useState(false);
  const countdownTimerRef = useRef<number | null>(null);
  const [styleFillsViewport, setStyleFillsViewport] = useState(false);
  const [backgroundSelections, setBackgroundSelections] = useState<Record<string, string | null>>({});
  const styleInView = true;
  const scrollToHistory = useCallback(() => {
    if (!historySectionRef.current) {
      return;
    }
    historySectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleAutoAdvance = useCallback(() => {
    if (autoAdvanceInFlight || !hasNextPage) {
      return;
    }
    setAutoAdvanceInFlight(true);
    void fetchNextPage()
      .catch(() => undefined)
      .finally(() => {
        setAutoAdvanceInFlight(false);
      });
  }, [autoAdvanceInFlight, fetchNextPage, hasNextPage]);

  const selectedStyle = useMemo(
    () => allStyles.find((style) => style.id === selectedStyleId) ?? null,
    [allStyles, selectedStyleId]
  );
  const selectedBackgroundColorCode = selectedStyle
    ? backgroundSelections[selectedStyle.id] ?? null
    : null;
  const selectedBackgroundColorValue = useMemo(() => {
    if (!selectedStyle || !selectedStyle.backgroundColors?.length) {
      return null;
    }
    const fallbackCode =
      selectedStyle.defaultBackgroundColorCode ??
      selectedStyle.backgroundColors[0]?.colorCode ??
      null;
    const resolvedCode =
      selectedBackgroundColorCode &&
        selectedStyle.backgroundColors.some((option) => option.colorCode === selectedBackgroundColorCode)
        ? selectedBackgroundColorCode
        : fallbackCode;
    if (!resolvedCode) {
      return null;
    }
    const match = selectedStyle.backgroundColors.find((option) => option.colorCode === resolvedCode);
    return match?.colorValue ?? resolvedCode;
  }, [selectedBackgroundColorCode, selectedStyle]);
  const selectedStyleCost = selectedStyle ? Math.max(selectedStyle.costMultiplier ?? 1, 1) : null;
  const activeQuota = quota ?? token?.quota ?? null;
  const insufficientCredits =
    selectedStyleCost !== null && activeQuota ? activeQuota.remaining < selectedStyleCost : false;
  const projectedRemaining =
    selectedStyleCost !== null && activeQuota ? activeQuota.remaining - selectedStyleCost : null;
  const lowBalance =
    !insufficientCredits && projectedRemaining !== null && projectedRemaining <= 3;
  const formattedCost =
    selectedStyleCost !== null ? numberFormatter.format(selectedStyleCost) : null;
  const costNotice =
    selectedStyleCost !== null && activeQuota
      ? insufficientCredits
        ? t('uploadPanel.costNotice.insufficient', {
          cost: formattedCost,
          remaining: numberFormatter.format(activeQuota.remaining)
        })
        : t('uploadPanel.costNotice.projected', {
          cost: formattedCost,
          projected: numberFormatter.format(Math.max(projectedRemaining ?? 0, 0))
        })
      : null;
  const costNoticeTone: 'default' | 'warning' | 'danger' = insufficientCredits
    ? 'danger'
    : lowBalance
      ? 'warning'
      : 'default';

  useEffect(() => {
    if (!allStyles.length) {
      setBackgroundSelections((prev) => {
        if (!Object.keys(prev).length) {
          return prev;
        }
        return {};
      });
      return;
    }
    setBackgroundSelections((prev) => {
      const next: Record<string, string | null> = {};
      let changed = false;

      allStyles.forEach((style) => {
        if (!style.backgroundColors?.length) {
          return;
        }
        const existing = prev[style.id] ?? null;
        const isValid =
          existing !== null &&
          style.backgroundColors.some((option) => option.colorCode === existing);
        const fallback =
          style.defaultBackgroundColorCode ??
          style.backgroundColors[0]?.colorCode ??
          null;
        next[style.id] = isValid ? existing : fallback;
        if (next[style.id] !== (prev[style.id] ?? null)) {
          changed = true;
        }
      });

      const prevRelevantKeys = Object.keys(prev).filter((key) =>
        allStyles.some((style) => style.id === key && style.backgroundColors?.length)
      );
      if (prevRelevantKeys.length !== Object.keys(next).length) {
        changed = true;
      }

      if (!changed) {
        return prev;
      }
      return next;
    });
  }, [allStyles]);

  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl(null);
      return;
    }

    const createObjectURL = typeof URL !== 'undefined' ? URL.createObjectURL : undefined;
    if (typeof createObjectURL !== 'function') {
      setPreviewUrl(null);
      return;
    }

    const objectUrl = createObjectURL(selectedFile);
    setPreviewUrl(objectUrl);

    return () => {
      if (typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [selectedFile]);

  const evaluateStyleHeight = useCallback(() => {
    if (typeof window === 'undefined') {
      setStyleFillsViewport(true);
      return;
    }
    const element = stylePickerRef.current;
    if (!element) {
      setStyleFillsViewport(false);
      return;
    }
    const rect = element.getBoundingClientRect();
    setStyleFillsViewport(rect.height <= window.innerHeight);
  }, []);

  useEffect(() => {
    evaluateStyleHeight();
  }, [evaluateStyleHeight, visibleStyles.length]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.addEventListener('resize', evaluateStyleHeight);
    return () => {
      window.removeEventListener('resize', evaluateStyleHeight);
    };
  }, [evaluateStyleHeight]);

  const scrollUploadPanelIntoView = useCallback(
    (options?: { force?: boolean }) => {
      if (typeof window === 'undefined') {
        return;
      }
      const panel = uploadPanelRef.current;
      if (!panel) {
        return;
      }
      const isLargeScreen = window.matchMedia('(min-width: 1024px)').matches;
      if (!options?.force && isLargeScreen) {
        return;
      }
      const rect = panel.getBoundingClientRect();
      const viewportHeight =
        typeof window.innerHeight === 'number'
          ? window.innerHeight
          : document.documentElement.clientHeight;
      const isFullyVisible = rect.top >= 0 && rect.bottom <= viewportHeight;
      if (!isFullyVisible || options?.force) {
        panel.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
    []
  );

  useEffect(() => {
    if (!selectedStyleId) {
      return;
    }
    scrollUploadPanelIntoView();
  }, [selectedStyleId, scrollUploadPanelIntoView]);

  const formatFileSize = useCallback((bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    const units = ['KB', 'MB', 'GB'];
    let value = bytes / 1024;
    let unitIndex = 0;

    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex += 1;
    }

    return `${value.toFixed(value >= 100 ? 0 : 1)} ${units[unitIndex]}`;
  }, []);

  const addHistoryEntry = useHistoryStore((state) => state.addEntry);

  const persistHistoryEntry = useCallback(
    async (
      file: File,
      style: StyleOption,
      tokenType: 'device' | 'user',
      resultDataUrl?: string,
      resultMimeType?: string | null,
      completedAt?: string
    ) => {
      try {
        const entry = await buildHistoryEntry({
          file,
          style,
          tokenType,
          resultDataUrl,
          resultMimeType,
          completedAt
        });
        addHistoryEntry(entry);
      } catch (error) {
        console.warn('Failed to persist history entry', error);
      }
    },
    [addHistoryEntry]
  );

  const handleHistoryOriginalSelect = useCallback(
    async (entry: HistoryEntry) => {
      if (typeof window === 'undefined' || typeof File === 'undefined') {
        return;
      }
      if (isInteractionLocked) {
        return;
      }
      try {
        const response = await fetch(entry.original.dataUrl);
        const blob = await response.blob();
        const mimeType = blob.type || 'image/png';
        const baseName = entry.original.fileName && entry.original.fileName.trim().length
          ? entry.original.fileName.trim()
          : `${entry.style.id}-${entry.id}`;
        const hasExtension = /\.[a-z0-9]+$/i.test(baseName);
        const fileName = hasExtension ? baseName : `${baseName}.png`;
        const reusedFile = new File([blob], fileName, { type: mimeType });
        refreshNetworkStatusSnapshot();
        setSelectedFile(reusedFile);
        setConvertedFromHeic(false);
        setIsPreparingImage(false);
        setConversionMessage(null);
        setEtaSeconds(undefined);
        setErrorMessage(undefined);
        setProcessingState('idle');
        if (!selectedStyle || selectedStyle.id !== entry.style.id) {
          const matchedStyle = allStyles.find((style) => style.id === entry.style.id);
          if (matchedStyle) {
            setSelectedStyleId(matchedStyle.id);
          }
        }
        requestAnimationFrame(() => {
          scrollUploadPanelIntoView({ force: true });
        });
      } catch (error) {
        console.warn('Failed to reuse history image', error);
      }
    },
    [
      allStyles,
      isInteractionLocked,
      selectedStyle,
      scrollUploadPanelIntoView,
      setSelectedFile,
      setConvertedFromHeic,
      setIsPreparingImage,
      setConversionMessage,
      setEtaSeconds,
      setErrorMessage,
      setProcessingState,
      setSelectedStyleId
    ]
  );

  const handleClearSelectedFile = useCallback(() => {
    if (isInteractionLocked) {
      return;
    }
    setSelectedFile(null);
    setPreviewUrl(null);
    setConvertedFromHeic(false);
    setIsPreparingImage(false);
    setConversionMessage(null);
    setEtaSeconds(undefined);
    setErrorMessage(undefined);
    setProcessingState('idle');
  }, [
    isInteractionLocked,
    setSelectedFile,
    setPreviewUrl,
    setConvertedFromHeic,
    setIsPreparingImage,
    setConversionMessage,
    setEtaSeconds,
    setErrorMessage,
    setProcessingState
  ]);

  const processUpload = useCallback(
    async (file: File, style: StyleOption, options?: ProcessUploadOptions) => {
      const fromQueue = options?.fromQueue ?? false;
      setProcessingState('uploading');
      setErrorMessage(undefined);
      setEtaSeconds(undefined);
      setSelectedFile(file);
      setSelectedStyleId(style.id);

      const startTime = typeof performance !== 'undefined' ? performance.now() : Date.now();
      const controller = new AbortController();
      setActiveUploadController(controller);

      const resolveBackgroundColorCode = () => {
        if (!style.backgroundColors?.length) {
          return null;
        }
        const preferred =
          options?.backgroundColorCode ??
          backgroundSelections[style.id] ??
          style.defaultBackgroundColorCode ??
          null;
        if (
          preferred &&
          style.backgroundColors.some((option) => option.colorCode === preferred)
        ) {
          return preferred;
        }
        return style.defaultBackgroundColorCode ?? style.backgroundColors[0]?.colorCode ?? null;
      };
      const backgroundColorCode = resolveBackgroundColorCode();

      try {
        const activeToken = await ensureToken();
        const result = await mutation.mutateAsync({
          file,
          styleId: style.id,
          token: activeToken,
          signal: controller.signal,
          backgroundColorCode: backgroundColorCode ?? undefined
        });

        setProcessingState('succeeded');
        setEtaSeconds(undefined);
        announce(t(`${transferNamespace}.announcements.processingCompleted`));
        await persistHistoryEntry(
          file,
          style,
          activeToken.tokenType,
          result.image.dataUrl,
          result.image.mimeType,
          new Date().toISOString()
        );
        requestAnimationFrame(() => {
          scrollToHistory();
        });
        const end = typeof performance !== 'undefined' ? performance.now() : Date.now();
        recordTransferMetric({
          phase: 'upload',
          durationMs: end - startTime,
          styleId: style.id,
          tokenType: activeToken.tokenType
        });
        const quotaSnapshot = activeToken.quota;
        if (quotaSnapshot && typeof result.remainingRequests === 'number') {
          const nextRemaining = Math.max(result.remainingRequests, 0);
          const nextStatus: Quota['status'] =
            nextRemaining <= 0 ? 'blocked' : nextRemaining <= 3 ? 'limited' : 'active';
          updateQuota({
            ...quotaSnapshot,
            remaining: nextRemaining,
            status: nextStatus
          });
        } else {
          setQuotaStatus('error');
        }
      } catch (error) {
        const aborted =
          (typeof DOMException !== 'undefined' &&
            error instanceof DOMException &&
            error.name === 'AbortError') ||
          (error instanceof Error && error.name === 'AbortError');
        if (aborted) {
          setProcessingState('idle');
          setErrorMessage(undefined);
          setEtaSeconds(undefined);
          mutation.reset();
          return;
        }

        setQuotaStatus('error');
        if (!fromQueue) {
          const message =
            error instanceof Error ? error.message : t(`${transferNamespace}.errors.generic`);
          setProcessingState('failed');
          setErrorMessage(message);
          announce(message);
        }
        throw error;
      } finally {
        setActiveUploadController((current) => (current === controller ? null : current));
      }
    },
    [
      announce,
      backgroundSelections,
      ensureToken,
      mutation,
      persistHistoryEntry,
      scrollToHistory,
      t,
      transferNamespace,
      updateQuota,
      setQuotaStatus
    ]
  );

  useEffect(() => {
    return () => {
      activeUploadController?.abort();
    };
  }, [activeUploadController]);

  useEffect(() => {
    if (processingState !== 'uploading') {
      setCountdownSeconds(null);
      setShowExtendedWaitNotice(false);
      if (countdownTimerRef.current) {
        window.clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
      return;
    }

    setShowExtendedWaitNotice(false);
    setCountdownSeconds(20);
    if (countdownTimerRef.current) {
      window.clearInterval(countdownTimerRef.current);
    }
    countdownTimerRef.current = window.setInterval(() => {
      setCountdownSeconds((previous) => {
        if (previous === null) {
          return previous;
        }
        if (previous <= 1) {
          if (countdownTimerRef.current) {
            window.clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
          }
          setShowExtendedWaitNotice(true);
          return 0;
        }
        return previous - 1;
      });
    }, 1000);

    return () => {
      if (countdownTimerRef.current) {
        window.clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
    };
  }, [processingState]);

  const cancelActiveUpload = useCallback(() => {
    if (!activeUploadController) {
      return;
    }
    activeUploadController.abort();
    setActiveUploadController(null);
  }, [activeUploadController]);

  const offlineQueue = useOfflineUploadQueue({
    autoFlush: true,
    onFlush: (upload) => {
      const style = allStyles.find((item) => item.id === upload.styleId);
      if (!style) {
        return Promise.resolve();
      }
      return processUpload(upload.file, style, {
        fromQueue: true,
        backgroundColorCode: upload.backgroundColorCode ?? null
      });
    }
  });
  const { queue, enqueue, dequeue } = offlineQueue;

  const resetError = useCallback(() => {
    setProcessingState('idle');
    setErrorMessage(undefined);
    setEtaSeconds(undefined);
  }, []);

  const handleBackgroundSelection = useCallback((styleId: string, colorCode: string) => {
    setBackgroundSelections((prev) => {
      if (prev[styleId] === colorCode) {
        return prev;
      }
      return {
        ...prev,
        [styleId]: colorCode
      };
    });
  }, []);

  const handleStyleSelect = useCallback(
    (style: StyleOption) => {
      if (isInteractionLocked) {
        return;
      }
      const wasSelected = selectedStyleId === style.id;
      setSelectedStyleId(wasSelected ? null : style.id);
      if (!wasSelected) {
        setProcessingState((state) => (state === 'failed' ? 'idle' : state));
        setErrorMessage(undefined);
        announce(t(`${transferNamespace}.announcements.styleSelected`, { style: style.name }));
      }
    },
    [announce, isInteractionLocked, selectedStyleId, t]
  );

  const handleFileSelect = useCallback(
    (file: File) => {
      void (async () => {
        refreshNetworkStatusSnapshot();
        const validation = validateImageFile(file);

        if (!validation.ok) {
          const message =
            validation.issue === 'size'
              ? t('uploadError.fileTooLarge', { limit: sizeLimitLabel })
              : t('uploadError.unsupportedType');
          setProcessingState('failed');
          setErrorMessage(message);
          setEtaSeconds(undefined);
          setSelectedFile(null);
          announce(message);
          return;
        }

        if (isInteractionLocked) {
          return;
        }

        setIsPreparingImage(false);
        setConversionMessage(null);

        const previousBaseName =
          selectedFile?.name ? selectedFile.name.replace(/\.[^.]+$/, '') : null;
        const isDerivedFromCrop =
          Boolean(previousBaseName) && file.name.startsWith(`${previousBaseName}-cropped`);

        let processedFile = file;
        let didConvertHeic = false;

        if (isHeicLikeFile(file)) {
          setIsPreparingImage(true);
          const convertingMessage = t('uploadPanel.conversion.heicInProgress');
          setConversionMessage(convertingMessage);
          announce(convertingMessage);
          try {
            processedFile = await convertHeicToJpeg(file);
            announce(t('uploadPanel.conversion.heicConvertedMeta'));
            didConvertHeic = true;
          } catch (error) {
            console.error('Failed to convert HEIC image', error);
            const message = t('uploadPanel.conversion.heicConversionFailed');
            setProcessingState('failed');
            setErrorMessage(message);
            setEtaSeconds(undefined);
            setSelectedFile(null);
            setConvertedFromHeic(false);
            announce(message);
            return;
          } finally {
            setConversionMessage(null);
            setIsPreparingImage(false);
          }
        }

        setSelectedFile(processedFile);
        setConvertedFromHeic((prev) => {
          if (didConvertHeic) {
            return true;
          }
          if (isDerivedFromCrop && prev) {
            return true;
          }
          return false;
        });
        setEtaSeconds(undefined);
        setErrorMessage(undefined);
        setProcessingState('idle');

        if (!isMemberVariant) {
          void ensureToken().catch((error) => {
            console.warn('Failed to prefetch device token', error);
          });
        }

        if (!selectedStyle) {
          announce(t(`${transferNamespace}.announcements.requireStyle`));
          return;
        }

        if (!gateAllowed) {
          const message = gateErrorKey
            ? t(gateErrorKey)
            : gateReasonKey
              ? t(gateReasonKey)
              : t(`${transferNamespace}.errors.generic`);
          setProcessingState('failed');
          setErrorMessage(message);
          announce(message);
          return;
        }
      })();
    },
    [
      announce,
      ensureToken,
      gateAllowed,
      gateErrorKey,
      gateReasonKey,
      isMemberVariant,
      selectedStyle,
      selectedFile,
      sizeLimitLabel,
      isInteractionLocked,
      t
    ]
  );

  const previewMeta =
    !isMemberVariant && selectedFile
      ? t('guestPage.preview.size', { size: formatFileSize(selectedFile.size) })
      : null;
  const guestQuotaBlocked =
    !isMemberVariant &&
    token?.tokenType === 'device' &&
    (token.quota.remaining <= 0 ||
      (selectedStyleCost !== null && token.quota.remaining < selectedStyleCost));

  const submitCurrentSelection = useCallback(() => {
    if (!selectedFile || !selectedStyle) {
      return;
    }

    if (!gateAllowed || guestQuotaBlocked || insufficientCredits) {
      return;
    }

    setProcessingState('uploading');
    setErrorMessage(undefined);
    setEtaSeconds(undefined);

    if (!getOnline()) {
      const alreadyQueued = queue.some(
        (item) => item.file === selectedFile && item.styleId === selectedStyle.id
      );
      if (!alreadyQueued) {
        enqueue(
          selectedFile,
          selectedStyle.id,
          selectedStyle.backgroundColors?.length ? selectedBackgroundColorCode ?? null : null
        );
      }
      announce(t(`${transferNamespace}.announcements.queuedOffline`));
      setProcessingState('idle');
      return;
    }

    void processUpload(selectedFile, selectedStyle, {
      backgroundColorCode: selectedStyle.backgroundColors?.length
        ? selectedBackgroundColorCode ?? null
        : null
    }).catch(() => undefined);
  }, [
    announce,
    gateAllowed,
    getOnline,
    enqueue,
    processUpload,
    queue,
    selectedFile,
    selectedStyle,
    selectedBackgroundColorCode,
    t,
    guestQuotaBlocked,
    insufficientCredits
  ]);

  const canRetryUpload = Boolean(
    selectedFile && selectedStyle && (isMemberVariant ? gateAllowed : true)
  );

  const renderUploadPanel = useCallback(
    (extraDisabled: boolean) => (
      <UploadPanel
        onSelectFile={handleFileSelect}
        onClearSelectedFile={selectedFile ? handleClearSelectedFile : undefined}
        disabled={mutation.isPending || extraDisabled}
        isUploading={mutation.isPending && processingState === 'uploading'}
        isPreparingImage={isPreparingImage}
        preparingMessage={conversionMessage}
        selectedStylePreviewUrl={selectedStyle?.previewUrl ?? null}
        selectedStyleName={selectedStyle?.name ?? null}
        selectedStyleBackground={selectedBackgroundColorValue}
        onClearSelectedStyle={
          selectedStyle ? () => handleStyleSelect(selectedStyle) : undefined
        }
        selectedFile={selectedFile}
        onSubmitSelection={
          selectedFile &&
            selectedStyle &&
            (isMemberVariant ? gateAllowed && !insufficientCredits : !guestQuotaBlocked && !insufficientCredits)
            ? submitCurrentSelection
            : undefined
        }
        onCancelUpload={
          mutation.isPending && processingState === 'uploading' ? cancelActiveUpload : undefined
        }
        countdownSeconds={mutation.isPending && processingState === 'uploading' ? countdownSeconds : null}
        showExtendedWaitNotice={mutation.isPending && processingState === 'uploading' ? showExtendedWaitNotice : false}
        canSubmit={Boolean(
          selectedFile &&
          selectedStyle &&
          (isMemberVariant ? gateAllowed && !insufficientCredits : !guestQuotaBlocked && !insufficientCredits)
        )}
        costNotice={costNotice}
        costNoticeTone={costNoticeTone}
        previewUrl={previewUrl}
        previewName={selectedFile?.name ?? null}
        previewMeta={previewMeta}
        convertedFromHeic={convertedFromHeic}
        showError={processingState === 'failed'}
        errorMessage={processingState === 'failed' ? errorMessage : undefined}
        onRetryError={
          processingState === 'failed'
            ? canRetryUpload
              ? submitCurrentSelection
              : resetError
            : null
        }
      />
    ),
    [
      gateAllowed,
      cancelActiveUpload,
      handleFileSelect,
      handleStyleSelect,
      isMemberVariant,
      mutation.isPending,
      countdownSeconds,
      previewMeta,
      previewUrl,
      processingState,
      convertedFromHeic,
      selectedFile,
      selectedStyle,
      guestQuotaBlocked,
      costNotice,
      costNoticeTone,
      insufficientCredits,
      showExtendedWaitNotice,
      isPreparingImage,
      conversionMessage,
      resetError,
      canRetryUpload,
      submitCurrentSelection,
      handleClearSelectedFile,
      selectedBackgroundColorValue
    ]
  );

  const queueTranslation = {
    title: t(`${transferNamespace}.queue.title`, { count: queue.length }),
    item: (fileName: string) => t(`${transferNamespace}.queue.item`, { file: fileName }),
    retrySingle: t(`${transferNamespace}.queue.retrySingle`)
  };
  const infiniteScrollConfig = {
    hasMore: Boolean(hasNextPage),
    autoAdvanceEnabled: Boolean(hasNextPage) && !stylesLoading && !isInteractionLocked,
    isLoadingMore: isFetchingNextPage || autoAdvanceInFlight,
    onLoadMore: handleAutoAdvance
  };

  const renderQueue = () =>
    queue.length ? (
      <div className="rounded-2xl border border-ai-surface-glow/20 bg-ai-surface-elevated/50 p-4 text-xs text-ai-text-secondary">
        <h4 className="mb-2 text-sm font-semibold text-ai-text-primary">
          {queueTranslation.title}
        </h4>
        <ul className="space-y-2">
          {queue.map((item) => (
            <li key={item.id} className="flex flex-col gap-1">
              <span>{queueTranslation.item(item.file.name)}</span>
              <button
                type="button"
                className="self-start rounded-full border border-ai-primary/40 px-3 py-1 text-[11px] font-semibold text-ai-primary transition-colors hover:bg-ai-primary/15 focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
                onClick={() => {
                  const style = allStyles.find((styleOption) => styleOption.id === item.styleId);
                  if (!style) return;
                  processUpload(item.file, style, {
                    fromQueue: true,
                    backgroundColorCode: item.backgroundColorCode ?? null
                  })
                    .then(() => dequeue(item.id))
                    .catch(() => undefined);
                }}
                disabled={isMemberVariant && !gateAllowed}
              >
                {queueTranslation.retrySingle}
              </button>
            </li>
          ))}
        </ul>
      </div>
    ) : null;

  const stylePickerSection = (
    <div
      ref={stylePickerRef}
      className={clsx(
        'order-2 lg:order-1',
        isMemberVariant ? 'w-full space-y-4 lg:min-h-full' : 'space-y-4'
      )}
    >
      <StyleSelector
        styles={visibleStyles}
        isLoading={stylesLoading}
        selectedStyleId={selectedStyleId}
        onSelect={handleStyleSelect}
        disabled={isInteractionLocked}
        backgroundSelections={backgroundSelections}
        onBackgroundSelect={handleBackgroundSelection}
        infiniteScroll={infiniteScrollConfig}
      />
    </div>
  );

  const uploadPanelClasses = clsx(
    'order-1 lg:order-2 mx-auto w-full max-w-lg transition-all duration-300 lg:mx-0 lg:ml-auto lg:self-start',
    styleInView
      ? [
        styleFillsViewport ? 'lg:sticky lg:top-1/2 lg:-translate-y-1/2' : 'lg:sticky lg:top-24',
        styleFillsViewport
          ? 'lg:max-h-[calc(100vh-3rem)]'
          : 'lg:max-h-[calc(100vh-6rem)]',
        'lg:overflow-y-auto lg:pr-3'
      ]
      : 'lg:opacity-0 lg:pointer-events-none'
  );

  const uploadPanelSection = (
    <div
      ref={uploadPanelRef}
      className={uploadPanelClasses}
    >
      <UploadGate allowed={gateAllowed} reasonKey={gateReasonKey}>
        {({ disabled }) => renderUploadPanel(disabled || isPreparingImage)}
      </UploadGate>
    </div>
  );

  const processingAsideSection = (
    <aside
      className={clsx(
        'order-3',
        isMemberVariant ? 'space-y-5' : 'space-y-5 lg:sticky lg:top-20 lg:self-start'
      )}
    >
      <ProcessingStatus
        state={processingState}
        etaSeconds={etaSeconds}
        errorMessage={errorMessage}
      />
      {renderQueue()}
    </aside>
  );

  const experienceLayout = (
    <section className="relative rounded-3xl border border-ai-surface-glow/30 bg-ai-surface-elevated/60 p-2 shadow-ai-card lg:p-3">
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        {stylePickerSection}
        {uploadPanelSection}
        {processingAsideSection}
      </div>
    </section>
  );

  return (
    <>
      {isMemberVariant ? <QuotaBanner className="mb-4" /> : null}
      <HistoryPanel ref={historySectionRef} onSelectOriginal={handleHistoryOriginalSelect} />
      <div className="flex space-x-6 items-center">
        <h2 className="text-lg font-semibold text-ai-text-primary">
          {t(`${transferNamespace}.stylePicker.title`)}
        </h2>
        <p className="text-xs uppercase tracking-[0.3em] text-ai-text-secondary">
          {t(`${transferNamespace}.stylePicker.caption`, {
            count: totalReportedStyles
          })}
        </p>
      </div>
      {experienceLayout}
    </>
  );
}
